import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { apis } from "../apis";
import { useDropzone } from "react-dropzone";
import { 
  Edit, 
  Trash2, 
  RefreshCw, 
  Plus,
  ArrowLeft,
  Star
} from "lucide-react";
import "./Dashboard.css";

// Custom Components
const Card = ({ children, className = "" }) => (
  <div className={`card ${className}`}>
    {children}
  </div>
);

const Button = ({ children, onClick, variant = "primary", disabled, className = "", icon }) => (
  <button 
    className={`btn btn-${variant} ${className}`} 
    onClick={onClick} 
    disabled={disabled}
  >
    {icon && <span className="btn-icon">{icon}</span>}
    {children}
  </button>
);

const Input = ({ label, value, onChange, error, helperText, type = "text" }) => (
  <div className="input-group">
    <label>{label}</label>
    <input
      type={type}
      value={value}
      onChange={onChange}
      className={error ? "error" : ""}
    />
    {helperText && <span className="helper-text">{helperText}</span>}
    {error && <span className="error-text">{error}</span>}
  </div>
);

const Modal = ({ isOpen, onClose, title, children }) => {
  if (!isOpen) return null;
  
  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h3>{title}</h3>
          <button className="close-button" onClick={onClose}>&times;</button>
        </div>
        <div className="modal-body">
          {children}
        </div>
      </div>
    </div>
  );
};

const Tabs = ({ activeTab, onTabChange, tabs }) => (
  <div className="tabs">
    {tabs.map((tab, index) => (
      <button
        key={index}
        className={`tab ${activeTab === index ? 'active' : ''}`}
        onClick={() => onTabChange(index)}
      >
        {tab}
      </button>
    ))}
  </div>
);

const AlbumCard = ({ album, onEdit, onDelete }) => {
  const navigate = useNavigate();

  const handleCardClick = (e) => {
    // Only navigate if the click wasn't on a button
    if (!e.target.closest('.album-actions')) {
      navigate(`/stories?id=${album.id}&name=${encodeURIComponent(album.name)}`);
    }
  };

  return (
    <div className="album-card" onClick={handleCardClick}>
      <div className="album-image">
        <img src={album.image} alt={album.title} />
      </div>
      <div className="album-content">
        <h3>{album.title}</h3>
        <div className="album-colors">
          {album.color.split(",").map((color, index) => (
            <div
              key={index}
              className="color-swatch"
              style={{ backgroundColor: color }}
            />
          ))}
        </div>
        <div className="album-stats">
          <span>{album.item_count} قصة</span>
        </div>
        <div className="album-actions">
          <button
            className="btn btn-secondary edit-button"
            onClick={(e) => {
              e.stopPropagation();
              onEdit(album);
            }}
          >
            <Edit size={16} />
            تعديل
          </button>
          <button
            className="btn btn-danger"
            onClick={(e) => {
              e.stopPropagation();
              onDelete(album);
            }}
          >
            <Trash2 size={16} />
            حذف
          </button>
        </div>
      </div>
    </div>
  );
};

const UserCard = ({ user }) => (
  <div className="user-card">
    <div className="user-header">
      <div className="user-avatar">
        {user.user_name.charAt(0).toUpperCase()}
      </div>
      <div className="user-info">
        <h3>{user.user_name}</h3>
        <p>{user.email}</p>
      </div>
    </div>
    <div className="user-kids">
      <h4>الأطفال</h4>
      <div className="kids-list">
        {user.kids.map(kid => {
          console.log(kid);
          return (
          <div key={kid.kid_id} className="kid-item">
            <div className="kid-avatar">
              {kid.gender === 'boy' ? '👦' : '👧'}
            </div>
            <div className="kid-info">
              <span className="kid-name">{kid.kid_name}</span>
              <span className="kid-dob">{kid.kid_dob}</span>
            </div>
          </div>
        )})}
      </div>
    </div>
   
  </div>
);

const ImageUpload = ({ onUpload, currentImage }) => {
  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    accept: "image/*",
    onDrop: onUpload,
  });

  return (
    <div {...getRootProps()} className={`image-upload ${isDragActive ? 'drag-active' : ''}`}>
      <input {...getInputProps()} />
      {currentImage ? (
        <img src={currentImage} alt="Preview" className="preview-image" />
      ) : (
        <div className="upload-placeholder">
          <span>اسحب الصورة هنا أو انقر للتحميل</span>
        </div>
      )}
    </div>
  );
};

const Toast = ({ message, type, onClose }) => (
  <div className={`toast toast-${type}`}>
    <span>{message}</span>
    <button onClick={onClose}>&times;</button>
  </div>
);

const Dashboard = () => {
  const [activeTab, setActiveTab] = useState(0);
  const [albums, setAlbums] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState({
    albums: true,
    users: true,
    action: false
  });
  const [error, setError] = useState({ message: "", type: "error" });
  const [editingAlbum, setEditingAlbum] = useState(null);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [newAlbum, setNewAlbum] = useState({ name: "", color: "", image: "" });
  const [deleteConfirmationOpen, setDeleteConfirmationOpen] = useState(false);
  const [albumToDelete, setAlbumToDelete] = useState(null);
  const navigate = useNavigate();

  const fetchAlbums = async () => {
    try {
      setLoading(prev => ({ ...prev, albums: true }));
      const data = await apis.getAlbums();
      setAlbums(data.data);
    } catch (error) {
      setError({ message: "فشل في تحميل الالبومات", type: "error" });
    } finally {
      setLoading(prev => ({ ...prev, albums: false }));
    }
  };

  const fetchUsers = async () => {
    try {
      setLoading(prev => ({ ...prev, users: true }));
      const data = await apis.getUsers();
      console.log(data.data);
      const groupedUsers = data.data.reduce((acc, kid) => {
        const userIndex = acc.findIndex((user) => user.user_id === kid.user_id);
        if (userIndex > -1) {
          acc[userIndex].kids.push({
            kid_id: kid.kid_id,
            kid_name: kid.kid_name,
            gender: kid.kid_gender,
            kid_dob: new Date(kid.kid_dob).toLocaleDateString(),
          });
        } else {
          acc.push({
            user_id: kid.user_id,
            user_name: kid.user_name,
            email: kid.email,
            last_login: new Date(kid.last_login).toLocaleString(),
            kids: [{
                kid_id: kid.kid_id,
                kid_name: kid.kid_name,
                gender: kid.kid_gender,
                kid_dob: new Date(kid.kid_dob).toLocaleDateString(),
            }],
          });
        }
        return acc;
      }, []);
      setUsers(groupedUsers);
    } catch (error) {
      setError({ message: "فشل في تحميل المستخدمين", type: "error" });
    } finally {
      setLoading(prev => ({ ...prev, users: false }));
    }
  };

  useEffect(() => {
    fetchAlbums();
    fetchUsers();
  }, []);

  const validateColor = (value) => {
    const colors = value.split(",");
    if (colors.length < 2) return false;
    const hexColorRegex = /^#([0-9A-Fa-f]{6}|[0-9A-Fa-f]{3})$/;
    return colors.every((color) => hexColorRegex.test(color.trim()));
  };

  const handleImageUpload = async (acceptedFiles) => {
    const file = acceptedFiles[0];
    if (!file) return;

    try {
      setLoading(prev => ({ ...prev, action: true }));
      const response = await apis.uploadFile(file);
      const imageUrl = response.url;

      if (editingAlbum) {
        setEditingAlbum(prev => ({ ...prev, image: imageUrl }));
      } else {
        setNewAlbum(prev => ({ ...prev, image: imageUrl }));
      }
    } catch (error) {
      setError({ message: "فشل في تحميل الصورة", type: "error" });
    } finally {
      setLoading(prev => ({ ...prev, action: false }));
    }
  };

  const handleSave = async () => {
    if (editingAlbum.color && !validateColor(editingAlbum.color)) {
      setError({ message: "تنسيق الألوان غير صحيح", type: "error" });
      return;
    }

    try {
      setLoading(prev => ({ ...prev, action: true }));
      await apis.editAlbum(editingAlbum.id, editingAlbum.name, editingAlbum.color, editingAlbum.image);
      await fetchAlbums();
      setError({ message: "تم تحديث الالبوم بنجاح", type: "success" });
      setIsEditModalOpen(false);
    } catch (error) {
      setError({ message: "فشل في تحديث الالبوم", type: "error" });
    } finally {
      setLoading(prev => ({ ...prev, action: false }));
    }
  };

  const handleAddAlbum = async () => {
    if (!newAlbum.name || !newAlbum.color) {
      setError({ message: "يرجى ملء جميع الحقول المطلوبة", type: "error" });
      return;
    }

    if (newAlbum.color && !validateColor(newAlbum.color)) {
      setError({ message: "تنسيق الألوان غير صحيح", type: "error" });
      return;
    }

    try {
      setLoading(prev => ({ ...prev, action: true }));
      await apis.addAlbum(newAlbum.name, newAlbum.color, newAlbum.image);
      await fetchAlbums();
      setError({ message: "تمت إضافة الالبوم بنجاح", type: "success" });
      setIsAddModalOpen(false);
    } catch (error) {
      setError({ message: "فشل في إضافة الالبوم", type: "error" });
    } finally {
      setLoading(prev => ({ ...prev, action: false }));
    }
  };

  const handleDelete = async () => {
    if (albumToDelete) {
      try {
        setLoading(prev => ({ ...prev, action: true }));
        await apis.deleteAlbum(albumToDelete.id);
        await fetchAlbums();
        setError({ message: "تم حذف الالبوم بنجاح", type: "success" });
        setDeleteConfirmationOpen(false);
        setAlbumToDelete(null);
      } catch (error) {
        setError({ message: "فشل في حذف الالبوم", type: "error" });
      } finally {
        setLoading(prev => ({ ...prev, action: false }));
      }
    }
  };

  return (
    <div className="dashboard">
      <header className="dashboard-header">
        <div className="header-content">
          <h1>لوحة التحكم</h1>
          <p>إدارة الالبومات والمستخدمين</p>
        </div>
        <Button 
          onClick={() => {
            fetchAlbums();
            fetchUsers();
          }}
          disabled={loading.action}
          icon={<RefreshCw size={18} />}
        >
          تحديث
        </Button>
      </header>

      <Tabs
        activeTab={activeTab}
        onTabChange={setActiveTab}
        tabs={["الالبومات", "المستخدمين"]}
      />

      {activeTab === 0 && (
        <div className="albums-section">
          <div className="section-header">
            <h2>الالبومات</h2>
            <Button 
              onClick={() => setIsAddModalOpen(true)}
              icon={<Plus size={18} />}
            >
              إضافة البوم
            </Button>
          </div>
          
          {loading.albums ? (
            <div className="loading-albums">
              {[1, 2, 3].map(i => (
                <div key={i} className="album-card-skeleton">
                  <div className="skeleton-image"></div>
                  <div className="skeleton-content">
                    <div className="skeleton-title"></div>
                    <div className="skeleton-colors"></div>
                    <div className="skeleton-actions"></div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="albums-grid">
              {albums.map(album => (
                <AlbumCard 
                  key={album.id}
                  album={album}
                  onEdit={(album) => {
                    setEditingAlbum(album);
                    setIsEditModalOpen(true);
                  }}
                  onDelete={(album) => {
                    setAlbumToDelete(album);
                    setDeleteConfirmationOpen(true);
                  }}
                />
              ))}
            </div>
          )}
        </div>
      )}

      {activeTab === 1 && (
        <div className="users-section">
          <div className="section-header">
            <h2>المستخدمين</h2>
          </div>
          
          {loading.users ? (
            <div className="loading-users">
              {[1, 2, 3].map(i => (
                <div key={i} className="user-card-skeleton">
                  <div className="skeleton-header"></div>
                  <div className="skeleton-kids"></div>
                  <div className="skeleton-footer"></div>
                </div>
              ))}
            </div>
          ) : (
            <div className="users-grid">
              {users.map(user => (
                <UserCard key={user.user_id} user={user} />
              ))}
            </div>
          )}
        </div>
      )}

      <Modal
        isOpen={isEditModalOpen}
        onClose={() => setIsEditModalOpen(false)}
        title="تعديل الالبوم"
      >
        {editingAlbum && (
          <div className="album-form">
            <Input
              label="اسم الالبوم"
              value={editingAlbum.name}
              onChange={(e) => setEditingAlbum(prev => ({ ...prev, name: e.target.value }))}
            />
            <Input
              label="الألوان"
              value={editingAlbum.color}
              onChange={(e) => setEditingAlbum(prev => ({ ...prev, color: e.target.value }))}
              helperText="أدخل ألوان HEX مفصولة بفواصل (مثال: #FF0000,#00FF00)"
            />
            <ImageUpload
              onUpload={handleImageUpload}
              currentImage={editingAlbum.image}
            />
            <div className="form-actions">
              <Button onClick={() => setIsEditModalOpen(false)} variant="secondary">
            إلغاء
          </Button>
              <Button onClick={handleSave} disabled={loading.action}>
                {loading.action ? "جاري الحفظ..." : "حفظ"}
          </Button>
            </div>
          </div>
        )}
      </Modal>

      <Modal
        isOpen={isAddModalOpen}
        onClose={() => setIsAddModalOpen(false)}
        title="إضافة البوم جديدة"
      >
        <div className="album-form">
          <Input
            label="اسم الالبوم"
            value={newAlbum.name}
            onChange={(e) => setNewAlbum(prev => ({ ...prev, name: e.target.value }))}
          />
          <Input
            label="الألوان"
            value={newAlbum.color}
            onChange={(e) => setNewAlbum(prev => ({ ...prev, color: e.target.value }))}
            helperText="أدخل ألوان HEX مفصولة بفواصل (مثال: #FF0000,#00FF00)"
          />
          <ImageUpload
            onUpload={handleImageUpload}
            currentImage={newAlbum.image}
          />
          <div className="form-actions">
            <Button onClick={() => setIsAddModalOpen(false)} variant="secondary">
            إلغاء
          </Button>
            <Button onClick={handleAddAlbum} disabled={loading.action}>
              {loading.action ? "جاري الإضافة..." : "إضافة"}
          </Button>
          </div>
        </div>
      </Modal>

      <Modal
        isOpen={deleteConfirmationOpen}
        onClose={() => setDeleteConfirmationOpen(false)}
        title="تأكيد الحذف"
      >
        <div className="delete-confirmation">
          <p>هل أنت متأكد أنك تريد حذف هذه الالبوم؟</p>
          <div className="form-actions">
            <Button onClick={() => setDeleteConfirmationOpen(false)} variant="secondary">
            إلغاء
          </Button>
            <Button onClick={handleDelete} variant="danger" disabled={loading.action}>
              {loading.action ? "جاري الحذف..." : "حذف"}
          </Button>
          </div>
        </div>
      </Modal>

      {error.message && (
        <Toast
          message={error.message}
          type={error.type}
          onClose={() => setError({ message: "", type: "error" })}
        />
      )}
    </div>
  );
};

export default Dashboard;
